import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Star, Quote, Check } from "lucide-react";

export const TestimonialPrompt = () => {
  const [state, setState] = useState({ eligible: false, testimonial: null });
  const [loaded, setLoaded] = useState(false);
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [content, setContent] = useState("");
  const [consent, setConsent] = useState(false);
  const [anonymous, setAnonymous] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get("/testimonials/me")
      .then(({ data }) => setState(data))
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  const submit = async () => {
    if (rating < 1) return toast.error("Ajoutez une note en étoiles.");
    if (content.trim().length < 10) return toast.error("Votre témoignage est un peu court.");
    if (!consent) return toast.error("Cochez l'autorisation d'affichage pour publier.");
    setSaving(true);
    try {
      const { data } = await api.post("/testimonials", {
        rating,
        content: content.trim(),
        display_consent: consent,
        anonymous,
      });
      setState({ eligible: true, testimonial: data });
      setOpen(false);
      toast.success("Merci ! Votre témoignage a été envoyé pour validation.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Envoi impossible.");
    } finally {
      setSaving(false);
    }
  };

  if (!loaded || !state.eligible) return null;

  if (state.testimonial) {
    return (
      <div
        data-testid="testimonial-submitted"
        className="bg-[#7A8B76]/10 border border-[#7A8B76]/30 rounded-3xl p-6 mb-8 flex items-center gap-3"
      >
        <Check className="w-5 h-5 text-[#7A8B76]" strokeWidth={2} />
        <p className="text-[#5C5754]">
          <span className="font-medium text-[#2C2A29]">Merci pour votre témoignage</span> — il sera
          publié après validation.
        </p>
      </div>
    );
  }

  return (
    <div
      data-testid="testimonial-prompt"
      className="bg-[#F3EFEB] border border-[#E6DCD3] rounded-3xl p-8 md:p-10 mb-8"
    >
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center">
          <Quote className="w-4 h-4 text-[#C86B5E]" strokeWidth={1.5} />
        </div>
        <h2 className="font-heading text-2xl">Votre expérience compte</h2>
      </div>
      {!open ? (
        <>
          <p className="text-[#5C5754] leading-relaxed mb-6 max-w-xl">
            Ce bilan vous a été utile ? Partagez quelques mots — votre témoignage pourra inspirer
            d'autres couples et célibataires à franchir le pas.
          </p>
          <button
            data-testid="open-testimonial-form"
            onClick={() => setOpen(true)}
            className="bg-[#C86B5E] hover:bg-[#B35A4E] text-white rounded-full px-8 py-3.5 transition-colors"
          >
            Laisser un témoignage
          </button>
        </>
      ) : (
        <div className="space-y-5">
          <div className="flex items-center gap-1" data-testid="testimonial-stars">
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                type="button"
                data-testid={`testimonial-star-${n}`}
                onMouseEnter={() => setHover(n)}
                onMouseLeave={() => setHover(0)}
                onClick={() => setRating(n)}
                className="p-1"
                aria-label={`${n} étoiles`}
              >
                <Star
                  className="w-7 h-7 transition-colors"
                  strokeWidth={1.5}
                  style={{
                    fill: (hover || rating) >= n ? "#D4A373" : "transparent",
                    color: (hover || rating) >= n ? "#D4A373" : "#C9C2BA",
                  }}
                />
              </button>
            ))}
          </div>
          <textarea
            data-testid="testimonial-content"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            rows={4}
            maxLength={800}
            placeholder="Ce que ce parcours vous a apporté…"
            className="w-full bg-white border border-[#E6E2DD] focus:border-[#C86B5E] focus:outline-none rounded-xl px-4 py-3 text-[#2C2A29] resize-none leading-relaxed"
          />
          <label className="flex items-start gap-3 text-sm text-[#5C5754] cursor-pointer">
            <input
              data-testid="testimonial-consent"
              type="checkbox"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              className="mt-1 accent-[#C86B5E]"
            />
            <span>J'autorise l'affichage public de mon témoignage sur le site.</span>
          </label>
          <label className="flex items-start gap-3 text-sm text-[#5C5754] cursor-pointer">
            <input
              data-testid="testimonial-anonymous"
              type="checkbox"
              checked={anonymous}
              onChange={(e) => setAnonymous(e.target.checked)}
              className="mt-1 accent-[#C86B5E]"
            />
            <span>Publier de façon anonyme (masquer mon prénom).</span>
          </label>
          <div className="flex items-center gap-3">
            <button
              data-testid="submit-testimonial"
              onClick={submit}
              disabled={saving}
              className="bg-[#C86B5E] hover:bg-[#B35A4E] disabled:opacity-50 text-white rounded-full px-8 py-3.5 transition-colors"
            >
              {saving ? "Envoi…" : "Envoyer mon témoignage"}
            </button>
            <button
              onClick={() => setOpen(false)}
              className="text-[#8A8480] hover:text-[#C86B5E] text-sm"
            >
              Annuler
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
